import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { PillButton, ControlledTextField, ControlledLabelledCheckBox, ControlledDateInput, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { IColdStorageFormValues } from '../../../@types/model/coldStorage/coldStorageFormValues';
import CustomReactHookFormDevTools from '../../../components/devtools/ReactHookFormDevTools';
import ColdStorageModelHelper from '../../../@types/model/coldStorage/coldStorageModelHelper';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useAppSelector } from '../../../@types/redux';
import OptionType from '../../../@types/model/optionType';

interface IColdStorageFormProps {
    isLoading : boolean;
    initialValues : IColdStorageFormValues;
    onSubmit : (values : IColdStorageFormValues) => void;
    onClose : () => void;
}

const ColdStorageScheduleDialog = (props : IColdStorageFormProps) : React.ReactElement => {
    const storageUnits = useAppSelector(x => x.masterData.storageUnits);
    const selectedSiteIds = useAppSelector(x => x.data.selectedSiteIds);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger, watch, setValue } = useForm<IColdStorageFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(ColdStorageModelHelper.formSchema()),
        mode: 'onChange',
    });

    const currentDurationHours = watch('expectedRunTimeHours');

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onIncrementDuration = (seconds : number) => {
        const currentDurationSeconds = currentDurationHours * 3600;

        const updatedDuration = currentDurationSeconds + seconds;
        const result = updatedDuration / 3600;

        setValue('expectedRunTimeHours', Number(result.toFixed(2)), { shouldValidate: true, shouldDirty: true });
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const storageUnitOptions = useMemo(() => {
        return storageUnits
            .filter(x => x.isActive && selectedSiteIds?.includes(x.siteId))
            .map(OptionType.fromDataModel);
    }, [storageUnits, selectedSiteIds]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20 pt15 pb10'}>
                    <ControlledMuiAutoCompleteSelect
                        className={'mb10'}
                        control={control}
                        label={'Storage Unit'}
                        name={'storageUnit'}
                        options={storageUnitOptions}
                        defaultValues={props.initialValues.storageUnit}
                        disabled
                    />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <ControlledDateInput
                            className={'p0 mb10'}
                            control={control}
                            label={'Issue Date'}
                            defaultValue={props.initialValues.startedOn}
                            name={'issueDate'}
                            size={'small'}
                        />
                    </LocalizationProvider>
                    <div className={'fdr wfill'}>
                        <ControlledTextField
                            className={'flx1'}
                            control={control}
                            label={'Duration (Hours)'}
                            name={'expectedRunTimeHours'}
                            defaultValue={props.initialValues.expectedRunTimeHours}
                            size={'small'}
                        />
                        {/* <Button
                            className={'cpd mb10'}
                            type={'button'}
                            onClick={() => onIncrementDuration(600)}
                        >
                            +10min
                        </Button> */}
                        <PillButton
                            className={'cpd mr10 ml10'}
                            text={'+30 min'}
                            size={'small'}
                            onClick={() => onIncrementDuration(1800)}
                            color={'secondary'}
                            disabled={ props.isLoading }
                        />
                        <PillButton
                            className={'cpd'}
                            text={'+1 Hour'}
                            size={'small'}
                            onClick={() => onIncrementDuration(3600)}
                            color={'secondary'}
                            disabled={ props.isLoading }
                        />
                    </div>
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox
                            label={'Active?'}
                            name={'isActive'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button
                            className={'cpd mr20'}
                            type={'button'}
                            onClick={props.onClose}
                        >
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default ColdStorageScheduleDialog;
